import {
  getResizedObjectsData,
  updateDataForTemplate,
  updateObject
} from '@frontend/group/modules/copy-and-resize/helpers';
import {getObjectsForOrder} from "@frontend/group/modules/copy-and-resize";
import {
  BACKGROUND_PROPERTIES,
  resizeBackgroundHandler
} from "@frontend/group/modules/copy-and-resize/handlers/resize-background-handler";
import {FIELD_NAME_IMAGE_AREA} from "@frontend/constants/name-fields-of-template";
import {copyAndResizeEventBroker, EVENTS} from "@frontend/group/modules/copy-and-resize/event-broker";
import {getClosestResizeReference, getResizeOrientation} from "@frontend/group/modules/copy-and-resize/resize-orientation";
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';

export const resizeAITemplateHandler = async (data) => {
  const {
    dimensions,
    preview,
    state: {
      objects = getObjectsForOrder(preview.canvas.getObjects()),
      resizeHint = '',
      orientationHints = {},
      referenceImages = [],
      backgroundProperties = {
        size: BACKGROUND_PROPERTIES.SIZE.COVER,
        position: BACKGROUND_PROPERTIES.POSITION.LEFT_AND_TOP,
        selectedField: objects.find(object => object.name === FIELD_NAME_IMAGE_AREA)?.id ?? null
      },
    },
    isCopyObject = false,
    beforeApplyResizedTemplate = null
  } = data;

  const { id, canvas, template } = preview;
  const originalDimension = { width: preview.dimension[0], height: preview.dimension[1] };
  const allObjects = canvas.getObjects();

  const freshObjects = getObjectsForOrder(preview.canvas.getObjects())

  const _objects = objects
    .filter(object => object.id !== backgroundProperties.selectedField)
    .map(object => freshObjects.find(fresh => fresh.id === object.target.id)?.target);

  const onlyBackground = _objects.length === 0;
  const selectedReferences = dimensions.map(dimension =>
    getClosestResizeReference(dimension, referenceImages)
  );
  const referenceData = new Map(onlyBackground ? [] : await Promise.all(
    [...new Set(selectedReferences.filter(Boolean))].map(async reference => [
      reference,
      await readReferenceImage(reference.file),
    ])
  ));
  const resizedTemplates = onlyBackground
    ? dimensions.map(dimension => ({ dimension, objects: [] }))
    : await Promise.all(
      dimensions.map((dimension, index) => getAIResizedTemplates({
        preview,
        dimension,
        objects: _objects,
        resizeHint: getResizeHintForDimension(dimension, resizeHint, orientationHints),
        referenceImage: referenceData.get(selectedReferences[index]) || '',
      }))
    );

  const resizedData = [];
  for (let index = 0; index < resizedTemplates.length; index++) {
    const resizedTemplate = resizedTemplates[index];

    updateDataForTemplate({ id, dimension: dimensions[index], template });

    if (typeof beforeApplyResizedTemplate === 'function') {
      await beforeApplyResizedTemplate(resizedTemplate);
    }

    applyAIResizedObjects({
      objects: _objects,
      resizedObjects: resizedTemplate.objects,
      isCopyObject,
      dimension: resizedTemplate.dimension,
      originalDimension,
    });

    resizeBackgroundHandler({
      isCopyObject,
      dimension: resizedTemplate.dimension,
      object: allObjects.find(object => object.id === backgroundProperties.selectedField),
      backgroundProperties: onlyBackground ? {
        ...backgroundProperties,
        size: BACKGROUND_PROPERTIES.SIZE.CONTAIN,
        position: BACKGROUND_PROPERTIES.POSITION.CENTER
      } : backgroundProperties
    });

    canvas.requestRenderAll();

    const objectData = getResizedObjectsData(preview.id, dimensions[index]);
    resizedData.push(objectData);
  }
  
  return resizedData;
}

const getResizeHintForDimension = (dimension, resizeHint, orientationHints) => {
  const orientation = getResizeOrientation(dimension);
  const general = resizeHint.trim();
  const specific = (orientationHints[orientation] || '').trim();

  return [
    general && `General instruction for all sizes:\n${general}`,
    specific && `${orientation} instruction for this size (takes priority over the general instruction):\n${specific}`
  ].filter(Boolean).join('\n\n');
}

export const getAIResizedTemplates = async ({ preview, dimension, objects, resizeHint = '', referenceImage = '' }) => {
  const originalTemplateDimension = {
    width: preview.dimension[0],
    height: preview.dimension[1],
  }

  const _objects = objects.map(obj => ({
    id: obj.id,
    left: obj.left,
    top: obj.top,
    width: obj.getScaledWidth(),
    height: obj.getScaledHeight(),
    type: obj.type,
    fieldType: obj.templateField?.type || obj.fieldType || null,
    src: obj.getSrc ? obj.getSrc() : null,
    objectName: (obj.name ?? obj.templateField?.name) || '',
    originX: obj.originX,
    originY: obj.originY,
    text: obj.text ? {
      value: obj.text,
      fontSize: obj.fontSize,
      color: obj.fill,
      lineHeight: obj.lineHeight,
      alignment: obj.textAlign,
    } : null,
    angle: obj.angle,
    boundingRect: obj.getBoundingRect(),
  }));
  const response = await axios.post('/banner/templates/resize', {
    originalDimension: originalTemplateDimension,
    dimension,
    objects: _objects,
    thumbnail: preview.canvas.toDataURL(),
    layerContactSheet: await createLayerContactSheet(objects),
    resizeHint,
    referenceImage,
  });
  
  copyAndResizeEventBroker.fire(EVENTS.PROGRESS, { dimension });
  
  return response.data;
}

const readReferenceImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const createLayerContactSheet = async (objects) => {
  const columns = 4;
  const tileWidth = 190;
  const tileHeight = 160;
  const canvas = document.createElement('canvas');
  canvas.width = columns * tileWidth;
  canvas.height = Math.ceil(objects.length / columns) * tileHeight;
  const context = canvas.getContext('2d');
  context.fillStyle = '#f1f3f5';
  context.fillRect(0, 0, canvas.width, canvas.height);

  await Promise.all(objects.map(async (object, index) => {
    const x = (index % columns) * tileWidth;
    const y = Math.floor(index / columns) * tileHeight;
    context.fillStyle = '#fff';
    context.fillRect(x + 4, y + 4, tileWidth - 8, tileHeight - 8);
    context.fillStyle = '#111';
    context.font = '12px sans-serif';
    context.fillText(object.id, x + 9, y + 22, tileWidth - 18);

    try {
      const url = object.toDataURL({ format: 'png' });
      const image = new Image();
      image.src = url;
      await image.decode();
      const scale = Math.min((tileWidth - 18) / image.width, (tileHeight - 40) / image.height);
      context.drawImage(image, x + (tileWidth - image.width * scale) / 2,
        y + 30 + (tileHeight - 40 - image.height * scale) / 2,
        image.width * scale, image.height * scale);
    } catch (error) {
      // A missing layer preview should not block resizing the other layers.
    }
  }));

  return canvas.toDataURL('image/jpeg', 0.85);
}

export const applyAIResizedObjects = ({ objects, resizedObjects, isCopyObject, dimension, originalDimension }) => {
  for (const resizedObject of resizedObjects) {
    const originalObject = objects.find(obj => obj.id === resizedObject.id);

    if (!originalObject) {
      continue;
    }

    const sourceBounds = originalObject.getBoundingRect(true, true);
    
    const object = isCopyObject ? _.cloneDeep(originalObject) : originalObject;
    
    object.set({
      ...(!object.type.includes('text') && {
        scaleX: (resizedObject.width / originalObject.getScaledWidth()) * originalObject.scaleX,
        scaleY: (resizedObject.height / originalObject.getScaledHeight()) * originalObject.scaleY,
      }),
      left: resizedObject.left,
      top: resizedObject.top,
    });

    if (object.type.includes('text')) {
      const requestedFontSize = resizedObject.text?.fontSize ?? object.fontSize;
      const newFontSize = object.isCurved
        ? requestedFontSize
        : Math.max(1, Math.floor(requestedFontSize));
      const newText = resizedObject.text?.value ?? object.text
      const newLineHeight = resizedObject.text?.lineHeight ?? object.lineHeight;
      const fontSizeScale = newFontSize / object.fontSize;
      const styles = object.generateResolvedStyleArray()
        .map(lineStyles => lineStyles.map(style => ({
          ...style,
          ...(style.fontSize && { fontSize: style.fontSize * fontSizeScale })
        })));
      
      object.set({
        text: newText,
        fontSize: newFontSize,
        width: Math.max(1, Math.floor(resizedObject.width)),
        lineHeight: newLineHeight,
        styles,
        scaleX: 1,
        scaleY: 1,
      });
    }

    object.setCoords();

    const isFullBleed = !object.type.includes('text')
      && sourceBounds.left <= originalDimension.width * 0.02
      && sourceBounds.top <= originalDimension.height * 0.02
      && sourceBounds.left + sourceBounds.width >= originalDimension.width * 0.98
      && sourceBounds.top + sourceBounds.height >= originalDimension.height * 0.98;
    fitObjectToCanvas(object, dimension, isFullBleed);

    updateObject(object);

    if (object.type.includes('text')) {
      const { updateLayoutChanges } = ShowPageScopeCollector.get(['updateLayoutChanges']);

      // The moving handler does not persist AI reflow for additional text fields.
      // Save the same text and leading that were used by the geometry check.
      updateLayoutChanges(object.canvas.instance_id, object.element_id, object.text);
      updateLayoutChanges(object.canvas.instance_id, `${object.element_id}_leading`, object.lineHeight);
      updateLayoutChanges(object.canvas.instance_id, `${object.element_id}_fontsize`, object.fontSize);
      updateLayoutChanges(object.canvas.instance_id, `${object.element_id}_width`, object.width);
      updateLayoutChanges(object.canvas.instance_id, `${object.element_id}_styles`, object.styles);
    }
  }
}

const fitObjectToCanvas = (object, dimension, isFullBleed) => {
  const bounds = object.getBoundingRect(true, true);

  if (!bounds.width || !bounds.height) return;

  const coversCanvas = bounds.left <= 0 && bounds.top <= 0
    && bounds.left + bounds.width >= dimension.width
    && bounds.top + bounds.height >= dimension.height;
  const fitsCanvas = bounds.left >= 0 && bounds.top >= 0
    && bounds.left + bounds.width <= dimension.width
    && bounds.top + bounds.height <= dimension.height;

  if (isFullBleed ? coversCanvas : fitsCanvas) return;

  const scale = isFullBleed
    ? Math.max(1, dimension.width / bounds.width, dimension.height / bounds.height)
    : Math.min(1, dimension.width / bounds.width, dimension.height / bounds.height);

  if (scale !== 1) {
    if (object.type.includes('text')) {
      const fontSize = object.isCurved
        ? object.fontSize * scale
        : Math.max(1, Math.floor(object.fontSize * scale));
      const fontScale = fontSize / object.fontSize;
      const styles = object.generateResolvedStyleArray()
        .map(lineStyles => lineStyles.map(style => ({
          ...style,
          ...(style.fontSize && { fontSize: style.fontSize * fontScale }),
        })));

      // Text is recreated from font size and width, without Fabric scale factors.
      object.set({
        fontSize,
        width: Math.max(1, Math.floor(object.width * scale)),
        styles,
      });
    } else {
      object.set({ scaleX: object.scaleX * scale, scaleY: object.scaleY * scale });
    }

    object.setCoords();
  }

  const finalBounds = object.getBoundingRect(true, true);
  const left = isFullBleed ? (dimension.width - finalBounds.width) / 2
    : Math.min(Math.max(0, finalBounds.left), dimension.width - finalBounds.width);
  const top = isFullBleed ? (dimension.height - finalBounds.height) / 2
    : Math.min(Math.max(0, finalBounds.top), dimension.height - finalBounds.height);

  object.set({
    left: object.left + left - finalBounds.left,
    top: object.top + top - finalBounds.top,
  });
  object.setCoords();
}
